import {
  useEffect,
  useState,
  useMemo,
} from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { shallowEqual } from "react-redux";

import BaseDataTable, {
  IColumnConfig,
  IDataRow,
} from "../../../components/ui/baseDataTable";

import SelectCell from "../../../components/ui/baseDataTable/cells/SelectCell";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import { selectChargesByLoanReferenceId, selectAssetsByChargeIds } from "../Logic/chargesSlice";
import { fetchLegalVerifications, upsertLegalVerifications } from "../Logic/legalVerificationThunk";
import { selectLegalVerificationsByChargeIds, selectLegalVerificationsStatus, selectLegalVerificationsError } from "../Logic/legalVerificationSlice";

import { FormType } from "../../../api/formConfigs";
import {
  LegalVerification,
  VerificationParentEntityType,
} from "../../../api/legalVerifications";
import FormConfigCell from "./FormConfigCell";
import { 
  VerificationTableSection, 
  VerificationEmptyTableBody, 
  SaveFooter 
} from "./VerificationContainer";
import { getSupportedLegalDocumentTypesForAssets, LEGAL_VERIFICATION_DOCUMENT_TYPE_LABELS } from "../Logic/legalVerificationMappings";
import { ANOMALY_SELECT_OPTIONS } from "./anomalyOptions";
import { selectFormConfigEntities } from "../Logic/formConfigsSlice";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";

export interface LegalVerificationRow extends LegalVerification {
  isAdded: boolean;
  isDirty: boolean;
}

interface LegalVerificationTableProps {
  loanReferenceId: string;
}

export const LegalVerificationTable: React.FC<
  LegalVerificationTableProps
> = ({ loanReferenceId }) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState(false);

  const legalStatus = useAppSelector(selectLegalVerificationsStatus);
  const legalError = useAppSelector(selectLegalVerificationsError);

  const chargesForLoan = useAppSelector(
    (state) => selectChargesByLoanReferenceId(state, loanReferenceId),
    shallowEqual
  );

  const chargeIds = useMemo(() => {
    return chargesForLoan.map((charge) => charge.charge_id);
  }, [chargesForLoan]);

  const legalVerifications = useAppSelector(
    (state) => selectLegalVerificationsByChargeIds(state, chargeIds),
    shallowEqual
  );

  const assetsForCharges = useAppSelector((state) =>
    selectAssetsByChargeIds(state, chargeIds)
  );

  const formConfigsMap = useAppSelector(selectFormConfigEntities);

  const chargeOptions = useMemo(() => {
    return chargesForLoan
      .map((charge) => {
        const secId = charge.registry_identifiers.security_interest_id;
        if (!secId) return null;
        return {
          name: secId,
          value: charge.charge_id,
        };
      })
      .filter((opt): opt is { name: string; value: string } => opt !== null);
  }, [chargesForLoan]);

  const [localData, setLocalData] = useState<LegalVerificationRow[]>([]);

  useEffect(() => {
    setLocalData(
      legalVerifications.map((row: LegalVerification) => ({
        ...row,
        isAdded: false,
        isDirty: false,
      }))
    );
  }, [legalVerifications, loanReferenceId]);

  const isFormDetailsValid = (details: Record<string, any> | undefined, formType?: FormType): boolean => {
    if (!formType) return true;
    const configData = formConfigsMap[formType];
    if (!configData?.configs?.fields) return true;

    const fields = configData.configs.fields;
    const values = details ?? {};

    return fields.every((field) => {
      if (!field.is_mandatory) return true;

      const value = values[field.key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  const hasChanges = useMemo(() => {
    return localData.some((row) => row.isDirty);
  }, [localData]);

  const hasValidationErrors = useMemo(() => {
    return localData
      .filter((row) => row.isDirty)
      .some((row) => {
        if (!row.parent_entity_id || !row.document_type) {
          return true;
        }

        const isPreloginValid = isFormDetailsValid(
          row.prelogin_details,
          row.document_type as string as FormType
        );
        const isCurrentValid = isFormDetailsValid(
          row.current_details,
          row.document_type as string as FormType
        );

        return !isPreloginValid || !isCurrentValid;
      });
  }, [localData, formConfigsMap]);

  const handleRetry = () => {
    if (caseId) {
      dispatch(fetchLegalVerifications(caseId));
    }
  };

  const handleAddRow = () => {
    const newRow: LegalVerificationRow = {
      verification_id: uuidv4(),
      parent_entity_type: VerificationParentEntityType.CHARGE,
      parent_entity_id: "",
      document_type: undefined as any,
      prelogin_details: {},
      current_details: {},
      anomaly: undefined,
      isAdded: true,
      isDirty: true,
    };
    setLocalData((prev) => [...prev, newRow]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    setLocalData((prev) => prev.filter((_, idx) => idx !== rowIndex));
  };

  const handleRowChange = (
    rowIndex: number,
    value: any,
    field: keyof LegalVerificationRow
  ) => {
    setLocalData((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIndex) return row;

        const updatedRow = { ...row, isDirty: true };

        if (field === "parent_entity_id") {
          updatedRow.parent_entity_id = value;
          updatedRow.document_type = undefined as any;
          updatedRow.prelogin_details = {};
          updatedRow.current_details = {};
        } else if (field === "document_type") {
          updatedRow.document_type = value;
          updatedRow.prelogin_details = {};
          updatedRow.current_details = {};
        } else {
          (updatedRow as any)[field] = value;
        }

        return updatedRow;
      })
    );
  };

  const handleSave = async () => {
    if (!caseId) return;

    const dirtyRows = localData.filter((row) => row.isDirty);
    if (dirtyRows.length === 0) return;

    try {
      setIsSaving(true);
      const verificationsToUpsert: LegalVerification[] = dirtyRows.map(
        ({ isDirty, isAdded, ...rest }) => rest
      );

      await dispatch(
        upsertLegalVerifications({
          case_id: caseId,
          request: {
            verifications: verificationsToUpsert,
          },
        })
      ).unwrap();

      showSuccessToast("Legal verifications saved successfully");
    } catch (error) {
      showErrorToast("Failed to save verifications. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setLocalData(
      legalVerifications.map((row: LegalVerification) => ({
        ...row,
        isAdded: false,
        isDirty: false,
      }))
    );
  };

  const getDocumentOptionsForRow = (row: LegalVerificationRow) => {
    if (!row.parent_entity_id) return [];

    const associatedAssets = assetsForCharges[row.parent_entity_id] || [];
    const documentTypes = getSupportedLegalDocumentTypesForAssets(associatedAssets);

    return documentTypes.map((type) => ({
      name: LEGAL_VERIFICATION_DOCUMENT_TYPE_LABELS[type] || type,
      value: type,
    }));
  };

  const dataRows: IDataRow<LegalVerificationRow>[] = useMemo(() => {
    return localData.map((row) => ({
      data: row,
      isEditable: true,
      widgets: row.isAdded ? ["add", "delete"] : ["add"],
      onAdd: handleAddRow,
      onDelete: handleDeleteRow,
    }));
  }, [localData]);

  const columnConfigs: IColumnConfig<LegalVerificationRow>[] = [
    {
      headerName: "Security",
      field: "parent_entity_id",
      type: "select",
      getValue: (row) => row.parent_entity_id ?? "",
      isEditable: (_, row) => row.isAdded,
      hideFocus: (row) => !row.isAdded,
      onChange: handleRowChange,
      options: chargeOptions,
      selectCellProps: {
        placeholder: "security",
        rootContainerClassName: "min-w-[120px] flex",
        className: "px-0 text-sm font-medium text-[#0A0A0A]",
        labelClassName: "px-0 py-1 min-w-[120px]",
        titleClassName: "font-medium text-[#0A0A0A]",
      },
    },
    {
      headerName: "Document Type",
      field: "document_type",
      type: "custom",
      getValue: (row) => row.document_type ?? "",
      isEditable: (_, row) => row.isAdded && !!row.parent_entity_id,
      hideFocus: (row) => !row.isAdded || !row.parent_entity_id,
      render: (rowIndex, value) => {
        const row = localData[rowIndex];
        const options = getDocumentOptionsForRow(row);
        const isDropdownEditable = row.isAdded && !!row.parent_entity_id;

        if (!row.parent_entity_id) {
          return (
            <div className="py-1 text-sm text-[#A3A3A3] select-none">
              Select type
            </div>
          );
        }

        return (
          <div className="min-w-[140px]">
            <SelectCell
              name="document type"
              value={value as string}
              isEditable={isDropdownEditable}
              options={options}
              className="px-0"
              labelClassName="px-0 py-1 text-xs w-full"
              onChange={(updatedVal) => handleRowChange(rowIndex, updatedVal, "document_type")}
            />
          </div>
        );
      },
    },
    {
      headerName: "Pre-Login State",
      field: "prelogin_details",
      type: "custom",
      getValue: (row) => row.prelogin_details,
      isEditable: true,
      render: (rowIndex, value) => {
        const row = localData[rowIndex];
        return (
          <FormConfigCell
            title="Pre-Login State"
            formType={row.document_type as string as FormType}
            disabled={!row.document_type}
            values={value}
            onChange={(updatedDetails) =>
              handleRowChange(rowIndex, updatedDetails, "prelogin_details")
            }
          />
        );
      },
    },
    {
      headerName: "Current State",
      field: "current_details",
      type: "custom",
      getValue: (row) => row.current_details,
      isEditable: true,
      render: (rowIndex, value) => {
        const row = localData[rowIndex];
        return (
          <FormConfigCell
            title="Current State"
            formType={row.document_type as string as FormType}
            disabled={!row.document_type}
            values={value}
            onChange={(updatedDetails) =>
              handleRowChange(rowIndex, updatedDetails, "current_details")
            }
          />
        );
      },
    },
    {
      headerName: "Anomaly",
      field: "anomaly",
      type: "select",
      getValue: (row) => row.anomaly ?? "None",
      isEditable: true,
      onChange: handleRowChange,
      options: ANOMALY_SELECT_OPTIONS,
      selectCellProps: {
        placeholder: "anomaly",
        className: "px-0",
        rootContainerClassName: "min-w-[90px] flex items-center",
        labelClassName: "px-0 py-1 text-xs min-w-[90px]",
      },
    },
  ];

  const isLoading = legalStatus === "loading" && !isSaving;

  return (
    <div className="w-full flex flex-col">
       <VerificationTableSection
        title="Legal Verifications"
        subtitle="Record legal verification details associated with security charges"
      >
        {isLoading ? (
          <CustomLoadingSpinner
            title="Loading Verifications..."
            subtitle="Fetching legal verifications associated with this case."
            minHeight="min-h-[160px]"
          />
        ) : legalError ? (
          <ErrorDisplay
            error="Unable to load legal verifications. Please try again."
            onRetry={handleRetry}
          />
        ) : (
          <BaseDataTable<LegalVerificationRow>
            columnConfigs={columnConfigs}
            dataRows={dataRows}
            showDefaultTableBody={dataRows.length === 0}
            defaultTableBody={
              <VerificationEmptyTableBody
                title="No legal verification records"
                subtitle="Add a document to a security to record its legal verification details"
                onAdd={handleAddRow}
              />
            }
          />
        )}
      </VerificationTableSection>

      {hasChanges && (
        <SaveFooter
          isSaving={isSaving}
          disabled={hasValidationErrors}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
        />
      )}
    </div>
  );
};
